import { FormEvent, useId, useState } from "react";
import { AlertCircle, ArrowRight, Check, LoaderCircle } from "lucide-react";

type FormState = "idle" | "loading" | "error" | "success";

interface WaitlistFormProps {
  source: "hero" | "final-cta";
  compact?: boolean;
}

const PAGE_LOADED_AT = Date.now();

export function WaitlistForm({ source, compact = false }: WaitlistFormProps) {
  const id = useId();
  const [state, setState] = useState<FormState>("idle");
  const [message, setMessage] = useState("We use your email only for early access. Unsubscribe any time.");
  const [email, setEmail] = useState("");

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    setState("loading");
    setMessage("Joining the early-access list…");

    const data = new FormData(form);
    const params = new URLSearchParams(window.location.search);
    const campaignSource = params.get("utm_source");

    try {
      const response = await fetch("/api/waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          company: data.get("company"),
          source: campaignSource ? `${source}:${campaignSource}` : source,
          referrer: document.referrer,
          elapsedMs: Date.now() - PAGE_LOADED_AT,
        }),
      });

      const result = (await response.json()) as { ok?: boolean; alreadyRegistered?: boolean; error?: string };
      if (!response.ok || !result.ok) {
        throw new Error(result.error || "We couldn’t save that email. Please try again.");
      }

      setState("success");
      setMessage(
        result.alreadyRegistered
          ? "You’re already on the early-access list."
          : "You’re on the early-access list. We’ll be in touch.",
      );
    } catch (error) {
      setState("error");
      setMessage(error instanceof Error ? error.message : "We couldn’t save that email. Please try again.");
    }
  }

  return (
    <form
      className={`waitlist-form${compact ? " waitlist-form--compact" : ""}`}
      data-state={state}
      onSubmit={handleSubmit}
      noValidate={false}
    >
      <div className="waitlist-form__field">
        <label htmlFor={`${id}-email`}>Email address</label>
        <div className="waitlist-form__control">
          <input
            id={`${id}-email`}
            name="email"
            type="email"
            autoComplete="email"
            inputMode="email"
            placeholder="you@company.com"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              if (state === "error" || state === "success") {
                setState("idle");
                setMessage("We use your email only for early access. Unsubscribe any time.");
              }
            }}
            aria-invalid={state === "error"}
            aria-describedby={`${id}-message`}
            disabled={state === "loading"}
            required
            maxLength={254}
          />
          {state === "error" && <AlertCircle aria-hidden="true" size={18} />}
          {state === "success" && <Check aria-hidden="true" size={18} />}
        </div>
      </div>

      <div className="waitlist-form__trap" aria-hidden="true" hidden>
        <label htmlFor={`${id}-company`}>Company website</label>
        <input id={`${id}-company`} name="company" type="text" tabIndex={-1} autoComplete="off" />
      </div>

      <button type="submit" className="button button--primary" disabled={state === "loading"}>
        {state === "loading" ? (
          <>
            <LoaderCircle className="spinner" aria-hidden="true" size={18} /> Joining…
          </>
        ) : state === "success" ? (
          <>
            <Check aria-hidden="true" size={18} /> You’re in
          </>
        ) : (
          <>
            Get early access <ArrowRight aria-hidden="true" size={18} />
          </>
        )}
      </button>

      <p id={`${id}-message`} className="waitlist-form__message" role="status" aria-live="polite">
        {message}
      </p>
      <p className="waitlist-form__no-spam">No spam. No generic career advice.</p>
    </form>
  );
}
